import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, delay, tap, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NotificationService } from '@progress/kendo-angular-notification';

// Import and re-export mock data types
import {
  scheduledReportsMockData,
  ScheduledReport as ScheduledReportType,
  getEnabledScheduledReports,
  getUpcomingScheduledReports,
} from 'src/app/features/customer/reporting-v2/components/scheduled/reporting-scheduled-mock-data/scheduled-reports-mock-data';

// Re-export ScheduledReport for external use
export type ScheduledReport = ScheduledReportType;

/**
 * Configuration for creating a new scheduled report
 */
export interface ScheduledReportConfig {
  report_type: string;
  customer_ids: number[];
  frequency: 'weekly' | 'monthly';
  day_of_week?: number; // 0-6 for weekly
  day_of_month?: number; // 1-31 for monthly
  time_of_day: string; // HH:MM format
  enabled: boolean;
  configuration: {
    include_executive_summary: boolean;
    include_detailed_findings: boolean;
    include_remediation: boolean;
    delivery_method: 'download' | 'email' | 'storage';
    email_recipients?: string[];
    storage_destination?: string;
  };
}

/**
 * Scheduled Report Service
 * Handles recurring report schedules and execution history
 */
@Injectable({
  providedIn: 'root',
})
export class ScheduledReportService {
  private http = inject(HttpClient);
  private notificationService = inject(NotificationService);
  private apiUrl = environment.apiUrl;

  /**
   * Get all scheduled reports
   * @returns Observable of scheduled report list
   */
  public getScheduledReports(): Observable<ScheduledReport[]> {
    // Mock implementation
    return of(scheduledReportsMockData).pipe(
      delay(300),
      tap(() => {
        console.log('[Mock] Fetched scheduled reports from backend');
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<ScheduledReport[]>(`${this.apiUrl}/v2/reporting/schedules/`);
  }

  /**
   * Get enabled scheduled reports only
   * @returns Observable of enabled scheduled report list
   */
  public getEnabledScheduledReports(): Observable<ScheduledReport[]> {
    // Mock implementation
    return of(getEnabledScheduledReports()).pipe(
      delay(300),
      tap(() => {
        console.log('[Mock] Fetched enabled scheduled reports from backend');
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<ScheduledReport[]>(`${this.apiUrl}/v2/reporting/schedules/?enabled=true`);
  }

  /**
   * Get upcoming scheduled reports (next N days)
   * @param days - Number of days to look ahead (default 7)
   * @returns Observable of upcoming scheduled report list
   */
  public getUpcomingScheduledReports(days: number = 7): Observable<ScheduledReport[]> {
    // Mock implementation
    return of(getUpcomingScheduledReports(days)).pipe(
      delay(300),
      tap(() => {
        console.log(`[Mock] Fetched upcoming scheduled reports (next ${days} days) from backend`);
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<ScheduledReport[]>(`${this.apiUrl}/v2/reporting/schedules/upcoming/?days=${days}`);
  }

  /**
   * Get scheduled report by ID
   * @param scheduleId - Schedule ID
   * @returns Observable of scheduled report
   */
  public getScheduledReportById(scheduleId: string): Observable<ScheduledReport | null> {
    // Mock implementation
    const schedule = scheduledReportsMockData.find((s) => s.id === scheduleId);
    return of(schedule || null).pipe(
      delay(300),
      tap(() => {
        console.log(`[Mock] Fetched scheduled report ${scheduleId} from backend`);
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.get<ScheduledReport>(`${this.apiUrl}/v2/reporting/schedules/${scheduleId}/`);
  }


  /**
   * Update an existing scheduled report
   * @param scheduleId - Schedule ID
   * @param updates - Schedule updates
   * @returns Observable of updated scheduled report
   */
  public updateScheduledReport(
    scheduleId: string,
    updates: Partial<ScheduledReportConfig>
  ): Observable<ScheduledReport> {
    // Mock implementation
    const existingSchedule = scheduledReportsMockData.find((s) => s.id === scheduleId);
    const updatedSchedule: ScheduledReport = {
      ...(existingSchedule || ({} as ScheduledReport)),
      ...updates,
      id: scheduleId,
      next_run:
        updates.frequency || updates.day_of_week || updates.day_of_month || updates.time_of_day
          ? this.calculateNextRun({ ...existingSchedule, ...updates } as any)
          : existingSchedule?.next_run || '',
    };

    return of(updatedSchedule).pipe(
      delay(500),
      tap((schedule) => {
        this.notificationService.show({
          content: `Schedule updated successfully! (Backend would PUT to /v2/reporting/schedules/${scheduleId}/)`,
          cssClass: 'success-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'success', icon: true },
          hideAfter: 5000,
        });
        console.log(`[Mock] Updated scheduled report ${scheduleId}:`, schedule);
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.put<ScheduledReport>(`${this.apiUrl}/v2/reporting/schedules/${scheduleId}/`, updates);
  }

  /**
   * Enable or disable a scheduled report
   * @param scheduleId - Schedule ID
   * @param enabled - Enable or disable
   * @returns Observable of updated scheduled report
   */
  public toggleScheduledReport(scheduleId: string, enabled: boolean): Observable<ScheduledReport> {
    // Mock implementation
    return this.updateScheduledReport(scheduleId, { enabled }).pipe(
      tap(() => {
        const action = enabled ? 'enabled' : 'disabled';
        this.notificationService.show({
          content: `Schedule ${action} successfully! (Backend would PATCH /v2/reporting/schedules/${scheduleId}/)`,
          cssClass: 'success-notification',
          animation: { type: 'fade', duration: 400 },
          position: { horizontal: 'center', vertical: 'top' },
          type: { style: 'success', icon: true },
          hideAfter: 5000,
        });
      })
    );

    // Production implementation (commented for prototype):
    // return this.http.patch<ScheduledReport>(`${this.apiUrl}/v2/reporting/schedules/${scheduleId}/`, { enabled });
  }

  /**
   * Delete a scheduled report
   * @param scheduleId - Schedule ID
   * @returns Observable of success
   */
  public deleteScheduledReport(scheduleId: number): Observable<void> {
     return this.http.delete<void>(`${this.apiUrl}/v2/reporting/api/report-schedules/${scheduleId}/`).pipe(
       delay(500),
       tap(() => {
         this.notificationService.show({
           content: `Schedule deleted successfully!`,
           cssClass: 'success-notification',
           animation: { type: 'fade', duration: 400 },
           position: { horizontal: 'center', vertical: 'top' },
           type: { style: 'success', icon: true },
           hideAfter: 5000,
         })
       })
      );
  }

  /**
   * Manually trigger a scheduled report to run now
   * @param scheduleId - Schedule ID
   * @returns Observable of created report job
   */

  public runScheduledReportNow(config: ReportJobConfigBase){
      return this.http.post<ReportJobConfig>(`${this.apiUrl}/v2/reporting/api/report-job/`, config);
  }

  /**
   * Helper method to calculate next run date
   */
  private calculateNextRun(config: Partial<ScheduledReportConfig>): string {
    const now = new Date();
    let nextRun = new Date(now);

    if (config.frequency === 'weekly' && config.day_of_week !== undefined) {
      // Calculate next occurrence of day_of_week
      const daysUntil = (config.day_of_week + 7 - now.getDay()) % 7 || 7;
      nextRun.setDate(now.getDate() + daysUntil);
    } else if (config.frequency === 'monthly' && config.day_of_month !== undefined) {
      // Calculate next occurrence of day_of_month
      nextRun.setDate(config.day_of_month);
      if (nextRun <= now) {
        nextRun.setMonth(nextRun.getMonth() + 1);
      }
    }

    // Set time of day
    if (config.time_of_day) {
      const [hours, minutes] = config.time_of_day.split(':').map(Number);
      nextRun.setHours(hours, minutes, 0, 0);
    }

    return nextRun.toISOString();
  }

  /**
   * Helper method to get report type display name
   */
  private getReportTypeName(reportType: string): string {
    const typeMap: { [key: string]: string } = {
      HIPAA_COMPLIANCE: 'HIPAA Compliance Report',
      NIST_800_171: 'NIST 800-171 Compliance Report',
      GENERAL_SECURITY: 'General Security Report',
      EXECUTIVE_REPORT: 'Executive Summary',
      MONTHLY_SERVICE_REVIEW: 'Monthly Service Review',
      VULNERABILITY_REPORT: 'Vulnerability Report',
      SOC_EVENTS_REPORT: 'SOC Events Report',
      ESCALATED_CASES_REPORT: 'Escalated Cases Report',
      SLA_ADHERENCE_DATA_REPORT: 'SLA Adherence Report',
      O365_ACCOUNT_ACTIVITY_REPORT: 'O365 Account Activity Report',
      XDR_INGESTION_DATA_REPORT: 'XDR Ingestion Report',
      WINDOW_ACCOUNT_ACTIVITY_REPORT: 'Windows Account Activity Report',
      AZURE_DEVICE_ACTIVITY_REPORT: 'Azure Device Activity Report',
    };
    return typeMap[reportType] || reportType;
  }

  /**
   * Helper method to get customer names for display
   */
  private getCustomerNames(customerIds: number[]): string[] {
    if (customerIds.length === 1) {
      return [`Customer ID: ${customerIds[0]}`];
    } else if (customerIds.length <= 3) {
      return customerIds.map((id) => `Customer ID: ${id}`);
    } else {
      return [`${customerIds.length} customers selected`];
    }
  }

  /**
   * Get scheduled reports in datatable format (for server-side datatable)
   * This mimics the backend API response format that datatables expects
   * @param params - Datatable parameters (page, page_size, search, ordering, filters)
   * @returns Observable of datatable response
   */
  public getScheduledReportsForDatatable(params: any): Observable<any> {
    // Mock implementation - simulates server-side pagination/filtering/sorting
    let data = [...scheduledReportsMockData];

    // Apply filters
    if (params.enabled !== undefined) {
      const enabled = params.enabled === 'True' || params.enabled === true;
      data = data.filter((r) => r.enabled === enabled);
    }

    if (params.frequency) {
      data = data.filter((r) => r.frequency === params.frequency);
    }

    // Apply search
    if (params.search) {
      const searchTerm = params.search.toLowerCase();
      data = data.filter(
        (r) =>
          r.report_type_name.toLowerCase().includes(searchTerm) ||
          r.customer_names.some((name) => name.toLowerCase().includes(searchTerm))
      );
    }

    // Apply sorting
    if (params.ordering) {
      const field = params.ordering.startsWith('-') ? params.ordering.substring(1) : params.ordering;
      const direction = params.ordering.startsWith('-') ? -1 : 1;

      data.sort((a: any, b: any) => {
        const aVal = a[field];
        const bVal = b[field];
        if (aVal < bVal) return -1 * direction;
        if (aVal > bVal) return 1 * direction;
        return 0;
      });
    }

    // Total records
    const recordsTotal = scheduledReportsMockData.length;
    const recordsFiltered = data.length;

    // Apply pagination
    const page = parseInt(params.page || '1');
    const pageSize = parseInt(params.page_size || '10');
    const start = (page - 1) * pageSize;
    const paginatedData = data.slice(start, start + pageSize);

    // Return in datatable format
    const response = {
      draw: params.draw || 1,
      recordsTotal,
      recordsFiltered,
      data: paginatedData,
    };

    return of(response).pipe(
      delay(300),
      tap(() => {
        console.log('[Mock] Fetched scheduled reports for datatable:', response);
      })
    );

    // Production implementation (commented for prototype):
    // const queryParams = new HttpParams({ fromObject: params });
    // return this.http.get<any>(`${this.apiUrl}/v2/reporting/schedules/datatable/`, { params: queryParams });
  }


  /**
   * Fetches all recipients by organization.
   * @returns {Observable<Recipient[]>} Emits an array containing only Recipient objects
   */
  public getRecipients(organizationId: number): Observable<Recipient[]> {
    const baseUrl = `${this.apiUrl}/v1/organization/administrative/organization(${organizationId})/list_users_in_organization/`;

    return this.http.get<{ value: any[] }>(baseUrl).pipe(
      map(response =>
        response.value.map(item => ({
          name: item.full_name,
          email: item.email
        }))
      )
    );
  }

public getScheduleReport(scheduleReportId: number): Observable<ReportJobConfig> {
    const baseUrl = `${this.apiUrl}/v2/reporting/api/report-schedules/${scheduleReportId}/`;

    return this.http.get<any>(baseUrl).pipe(
      map(item => ({
        organizations: item.params.organizations,
        start_date: item.params.start_date,
        end_date: item.params.end_date,
        report_template_id: item.report_template,
        delivery_method: 'download',
        primary_organization_id: item.primary_organization.id,
        schedule_type: item.schedule_type,
        aggregate_report: item.params.aggregate_report ?? false
      }))
    );
  }

}
export interface ReportSchedule {
  name: string;
  created_by: number;
  primary_organization_id: number;
  params: {
    recipients: Recipient[],
    organizations: number[],
    customer_id: number,
    start_date: string;
    end_date: string;
  },
  schedule_type: 'weekly' | 'monthly'
  schedule_config: {},
  enabled: true,
  report_template: number,
  next_run_at: string
}


export interface Recipient {
  name: string;
  email: string
}

export interface ReportJobConfigBase {
  organizations: number[];
  start_date: string;
  end_date: string;
  report_template_id: number;
  delivery_method: 'download';
  primary_organization_id: number;
  aggregate_report?: boolean;
}

export interface ReportJobConfig extends ReportJobConfigBase {
  schedule_type: 'weekly' | 'monthly';
}
